<template>
  <div class="min-h-screen flex flex-col">
    <!-- 背景装饰 -->
    <BackgroundPatterns />

    <!-- 顶部栏 -->
    <header
      class="fixed inset-x-0 top-0 z-50 bg-white/90 backdrop-blur-sm border-b border-gray-200/50"
    >
      <div class="max-w-8xl mx-auto px-4 sm:px-6 py-3">
        <div class="flex items-center justify-between">
          <router-link
            to="/"
            class="flex items-center h-8 hover:scale-105 transition-transform"
          >
            <img
              :src="systemLogo"
              alt="Aberdeen Institute"
              class="h-full w-auto object-contain"
            />
          </router-link>
          <div class="flex items-center gap-2">
            <LanguageSelector />
            <StatusIndicator class="hidden sm:inline-flex" />
          </div>
        </div>
      </div>
    </header>

    <!-- 主内容 -->
    <main
      class="flex-1 flex flex-col justify-center items-center px-4 sm:px-6 py-8 min-h-[calc(100vh-4rem)]"
    >
      <div class="w-full max-w-md sm:max-w-lg">
        <!-- 返回 -->
        <div class="mb-6">
          <router-link
            to="/login"
            class="inline-flex items-center text-sm font-medium text-gray-600 hover:text-gray-800 transition-colors"
          >
            <ArrowLeftIcon class="h-4 w-4 mr-2" />
            {{ t("common.back") }}
          </router-link>
        </div>

        <!-- 标题 -->
        <div class="text-center mb-8">
          <h2
            class="text-2xl sm:text-3xl font-bold tracking-tight text-gray-900 mb-3"
          >
            {{ t("passkey.welcomeLogin") }}
          </h2>
          <p class="text-sm sm:text-base text-gray-500">
            {{ t("passkey.loginSubtitle") }}
          </p>
        </div>

        <!-- 登录表单 -->
        <div
          class="bg-white/80 backdrop-blur-md rounded-2xl shadow-lg border border-white/20 p-8 sm:p-10"
        >
          <form
            @submit.prevent="handleAuthentication"
            class="space-y-5"
            method="post"
            action="#"
          >
            <!-- 用户名 -->
            <div>
              <label
                for="username"
                class="block text-sm font-medium text-gray-900 mb-2"
              >
                {{ t("common.username") }}
              </label>
              <input
                id="username"
                v-model="userId"
                name="username"
                type="text"
                autocomplete="username"
                required
                :disabled="loading"
                class="block w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-gray-900 placeholder:text-gray-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors"
                :placeholder="t('form.enterUsername')"
                aria-describedby="username-hint"
              />
              <p id="username-hint" class="mt-1 text-xs text-gray-500">
                {{ t("passkey.usernameHint") }}
              </p>

              <!-- 口令钥提示 -->
              <div
                class="mt-4 p-4 bg-gradient-to-r from-indigo-50 to-blue-50 rounded-xl border border-indigo-100"
              >
                <div class="flex items-start">
                  <div class="flex-shrink-0">
                    <div
                      class="h-10 w-10 rounded-full bg-indigo-100 flex items-center justify-center"
                    >
                      <FingerPrintIcon class="h-6 w-6 text-indigo-600" />
                    </div>
                  </div>
                  <div class="ml-3 flex-1">
                    <h3 class="text-sm font-medium text-indigo-900 mb-2">
                      {{ t("passkey.tips") }}
                    </h3>
                    <p class="text-xs text-indigo-700">
                      {{ t("passkey.tip2") }}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <!-- 提交按钮 -->
            <div class="pt-2">
              <button
                type="submit"
                :disabled="loading || !userId"
                class="group relative w-full flex justify-center items-center rounded-xl bg-gradient-to-r from-indigo-600 to-indigo-700 px-4 py-3 text-base font-semibold text-white shadow-lg hover:from-indigo-700 hover:to-indigo-800 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:from-indigo-400 disabled:to-indigo-400 disabled:cursor-not-allowed transition-all duration-200 hover:shadow-xl hover:-translate-y-0.5 active:translate-y-0"
              >
                <div
                  class="absolute inset-0 rounded-xl bg-white/20 opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                ></div>
                <span v-if="loading" class="mr-2 relative z-10">
                  <LoadingSpinner class="h-5 w-5 text-white" />
                </span>
                <FingerPrintIcon
                  v-if="!loading"
                  class="mr-2 h-5 w-5 relative z-10"
                />
                <span class="relative z-10">
                  {{ t("passkey.authenticate") }}
                </span>
              </button>
            </div>

            <!-- 其他选项 -->
            <div class="mt-6">
              <div class="relative">
                <div class="absolute inset-0 flex items-center">
                  <div class="w-full border-t border-gray-200"></div>
                </div>
                <div class="relative flex justify-center text-xs uppercase">
                  <span class="bg-white px-3 text-gray-500">
                    {{ isZhLocale ? "选项" : "options" }}
                  </span>
                </div>
              </div>

              <div class="mt-4 flex flex-col space-y-3">
                <!-- 返回登录 -->
                <router-link
                  to="/login"
                  class="inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-800 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors duration-200"
                >
                  <ArrowLeftIcon class="h-4 w-4 mr-2" />
                  {{ t("passkey.backToLogin") }}
                </router-link>
              </div>
            </div>
          </form>
        </div>
      </div>
    </main>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from "vue";
import { useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import { notificationService } from "@/services/notificationService";
import * as Sentry from "@sentry/vue";
import { FingerPrintIcon, ArrowLeftIcon } from "@heroicons/vue/24/outline";
import { useAuthStore } from "@/stores/auth";
import BackgroundPatterns from "@/components/layout/BackgroundPatterns.vue";
import LanguageSelector from "@/components/common/LanguageSelector.vue";
import StatusIndicator from "@/components/common/StatusIndicator.vue";
import LoadingSpinner from "@/components/common/LoadingSpinner.vue";
import { systemConfigService } from "@/services/systemConfigService";
import {
  getPasskeyAuthenticationOptions,
  finishPasskeyAuthentication,
  getPasskeyToken,
  base64ToArrayBuffer,
  arrayBufferToBase64,
} from "@/api/auth";
import { jwtService } from "@/services/jwt";

interface PublicKeyCredential extends Credential {
  rawId: ArrayBuffer;
  response: {
    clientDataJSON: ArrayBuffer;
    authenticatorData: ArrayBuffer;
    signature: ArrayBuffer;
    userHandle: ArrayBuffer | null;
  };
}

const router = useRouter();
const { t, locale } = useI18n();
const loading = ref(false);
const userId = ref("");

// 获取系统logo路径
const systemLogo = computed(() => {
  return systemConfigService.systemLogo;
});

// Computed properties
const isZhLocale = computed(
  () => locale.value === "zh-CN" || locale.value === "zh",
);

// 检查是否在安全上下文中运行
function isSecureContext() {
  return window.isSecureContext === true;
}

// 检查是否支持WebAuthn
function isWebAuthnSupported() {
  return (
    window.PublicKeyCredential !== undefined &&
    navigator.credentials !== undefined &&
    isSecureContext()
  );
}

// 处理WebAuthn认证
async function handleAuthentication() {
  // 在handleRegister和handleAuthentication函数中添加:
  if (!isWebAuthnSupported()) {
    if (!isSecureContext()) {
      throw new Error(t("passkey.unsecureContext"));
    }
    throw new Error(t("passkey.browserNotSupported"));
  }
  if (!userId.value) {
    notificationService.error("passkey.enterUsername");
    return;
  }

  loading.value = true;
  try {
    // 首先检查浏览器是否支持WebAuthn
    if (!window.PublicKeyCredential) {
      throw new Error(t("passkey.browserNotSupported"));
    }

    // 检查navigator.credentials是否可用
    if (!navigator.credentials) {
      throw new Error(t("passkey.apiNotAvailable"));
    }

    // 1. 获取认证选项
    const optionsData = await getPasskeyAuthenticationOptions(userId.value);
    console.log("完整的服务器响应:", JSON.stringify(optionsData, null, 2));

    // 尝试直接使用不同层次的数据
    let options;

    if (optionsData.publicKey?.publicKey) {
      options = optionsData.publicKey.publicKey;
      console.log("使用 optionsData.publicKey.publicKey");
    } else if (optionsData.publicKey) {
      options = optionsData.publicKey;
      console.log("使用 optionsData.publicKey");
    } else {
      options = optionsData;
      console.log("使用 optionsData");
    }

    if (!options) {
      console.error("无法获取有效选项:", optionsData);
      throw new Error(t("passkey.invalidOptions"));
    }

    // 解码challenge
    if (typeof options.challenge === "string" && options.challenge) {
      console.log("Challenge before decoding:", options.challenge);
      try {
        options.challenge = base64ToArrayBuffer(options.challenge);
      } catch (error) {
        console.error("Challenge解码失败:", error, options.challenge);
        throw new Error(t("passkey.invalidChallenge"));
      }
    } else {
      console.error("无效的challenge:", options.challenge);
      throw new Error(t("passkey.invalidChallenge"));
    }

    // 解码allowCredentials
    if (options.allowCredentials) {
      options.allowCredentials = options.allowCredentials.map(
        (credential: any) => ({
          ...credential,
          id: base64ToArrayBuffer(credential.id),
        }),
      );
    }

    console.log("处理后的认证选项:", options);

    // 3. 获取凭证
    try {
      const credential = (await navigator.credentials.get({
        publicKey: options,
      })) as PublicKeyCredential;

      if (!credential) {
        throw new Error(t("passkey.credentialGetFailed"));
      }

      // 4. 格式化凭证数据
      const loginData = {
        id: credential.id,
        rawId: arrayBufferToBase64(credential.rawId),
        userId: userId.value,
        type: credential.type,
        response: {
          clientDataJSON: arrayBufferToBase64(
            credential.response.clientDataJSON,
          ),
          authenticatorData: arrayBufferToBase64(
            credential.response.authenticatorData,
          ),
          signature: arrayBufferToBase64(credential.response.signature),
          userHandle: credential.response.userHandle
            ? arrayBufferToBase64(credential.response.userHandle)
            : null,
        },
      };

      console.log("认证数据已准备:", loginData);

      // 5. 完成认证过程
      const result = await finishPasskeyAuthentication(loginData);

      // 6. 处理登录成功
      if (result.success) {
        try {
          // 认证成功，获取JWT令牌
          console.log("Passkey认证成功，正在获取令牌...");
          const tokenResult = await getPasskeyToken(userId.value);

          if (tokenResult.access && tokenResult.refresh) {
            // 设置令牌
            await jwtService.setTokens(tokenResult.access, tokenResult.refresh);

            notificationService.success("passkey.loginSuccess");

            // 重定向到首页或指定的页面
            const redirect = router.currentRoute.value.query.redirect as string;
            if (redirect) {
              await router.replace(redirect);
            } else {
              await router.replace("/authorize");
            }
          } else {
            throw new Error(t("passkey.tokenError"));
          }
        } catch (tokenError) {
          console.error("获取Passkey令牌失败:", tokenError);
          throw new Error(t("passkey.tokenFetchError"));
        }
      } else {
        throw new Error(result.message || t("passkey.authenticationFailed"));
      }
    } catch (credError: unknown) {
      console.error("凭证获取错误:", credError);
      const errorMessage =
        credError instanceof Error ? credError.message : String(credError);
      throw new Error(`${t("passkey.browserError")}: ${errorMessage}`);
    }
  } catch (error: unknown) {
    console.error("Passkey认证错误:", error);

    Sentry.captureException(error, {
      tags: {
        component: "PasskeyLoginView",
        action: "authentication",
      },
      extra: {
        userId: userId.value,
      },
    });

    const errorMsg =
      error instanceof Error
        ? error.message
        : t("passkey.authenticationFailed");
    notificationService.error("passkey.error", errorMsg);
  } finally {
    loading.value = false;
  }
}
</script>

<style scoped>
/* Ensure main content is above background */
main {
  position: relative;
  z-index: 10;
}

/* Enhanced focus states */
input:focus-visible {
  outline: 2px solid rgb(99, 102, 241);
  outline-offset: 2px;
}

button:focus-visible {
  outline: 2px solid rgb(99, 102, 241);
  outline-offset: 2px;
}

/* Button hover effects */
button[type="submit"]:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 12px 28px -5px rgba(99, 102, 241, 0.4);
}

button[type="submit"]:active:not(:disabled) {
  transform: translateY(0);
}

/* Smooth transitions */
input,
button {
  transition: all 0.2s ease;
}

/* Header backdrop blur enhancement */
header {
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  z-index: 50;
}

/* Form card enhanced glass morphism effect */
.bg-white\/80 {
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  box-shadow:
    0 8px 32px -8px rgba(0, 0, 0, 0.12),
    0 0 0 1px rgba(255, 255, 255, 0.2),
    inset 0 1px 0 rgba(255, 255, 255, 0.3);
}

/* Input field enhancements */
input {
  transition: all 0.2s ease;
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
}

input:hover {
  border-color: rgb(147, 197, 253);
  background-color: rgba(255, 255, 255, 0.9);
}

input:focus {
  background-color: rgba(255, 255, 255, 0.95);
}

/* WebKit autofill styling */
input:-webkit-autofill {
  transition: background-color 50000s ease-in-out 0s;
  -webkit-box-shadow: 0 0 0 30px rgba(255, 255, 255, 0.9) inset !important;
  -webkit-text-fill-color: rgb(17, 24, 39) !important;
}

/* Passkey Animation */
.passkey-animation {
  position: relative;
  width: 80px;
  height: 48px;
}

.device-outline {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border: 2px solid #6366f1;
  border-radius: 8px;
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(99, 102, 241, 0.4);
  }
  70% {
    box-shadow: 0 0 0 4px rgba(99, 102, 241, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(99, 102, 241, 0);
  }
}

/* Improved button states */
button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none !important;
}

/* Responsive adjustments */
@media (max-width: 640px) {
  .max-w-md {
    max-width: 100%;
    padding: 0 1rem;
  }

  .bg-white\/80 {
    margin: 0 0.5rem;
    padding: 2rem 1.5rem !important;
  }

  /* Mobile: Adjust main container */
  main {
    min-height: calc(100vh - 3rem);
    padding: 1rem 1rem 2rem 1rem;
  }

  /* Mobile: Adjust spacing */
  .mb-8 {
    margin-bottom: 1.5rem;
  }

  /* Mobile: Adjust input fields */
  input {
    font-size: 16px; /* Prevent zoom on iOS */
  }
}

/* Subtle animation for the form card */
.bg-white\/80 {
  animation: slideIn 0.6s ease-out;
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
